/**
 * M1 の共通UI（05 §7）。
 *
 * ★**生の色値をここに書かない**＝すべて `tokens.css` の semantic トークン経由。
 *   見た目の正は Claude Design（D-45）で、確定したら **tokens.css 1ファイルの差し替え**で
 *   全画面に効く、という構造を保つための規律。色値そのものは TBD-11 で未確定。
 *
 * 状態の語彙（LC-08）もここに集約する＝卸と職人で違う語を使うが、**対応表は1か所**。
 */

import type { CSSProperties, ReactNode } from "react";

/* ------------------------------------------------------------------ */
/* 面・囲み                                                            */
/* ------------------------------------------------------------------ */

export function Card({
  children,
  padding = "var(--kz-space-5)",
  style,
  ...rest
}: {
  children: ReactNode;
  padding?: string;
  style?: CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      style={{
        background: "var(--kz-color-surface)",
        borderRadius: "var(--kz-radius-md)",
        // M1.7（D-91）＝**枠線を撤去して影だけで輪郭を出す**（HeroUI 方式）。
        // --kz-shadow-sm の3本目が「不透明度 0.3 の 1px」なので、border を引かなくても
        // フチが立つ。枠線と影を両方持つと縁が二重になって重く見えていた。
        //
        // ★**`border` を丸ごと消さずに「幅0の透明な実線」で残す**＝呼び出し側には
        //   `style={{ borderColor: "var(--kz-status-warn)", borderWidth: 2 }}` のように
        //   **色と太さだけ**を渡して意味のある枠（受注詳細の「要確認」・一度きりの設定URL）を
        //   出している箇所がある。`border-style` が `none` だと **`borderWidth: 2` も 0px に潰れ、
        //   その枠が黙って消える**（M1.7 の敵対レビューで実測・5箇所が該当した）。
        borderStyle: "solid",
        borderWidth: 0,
        borderColor: "transparent",
        boxShadow: "var(--kz-shadow-sm)",
        padding,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "var(--kz-space-4)",
        marginBottom: "var(--kz-space-5)",
      }}
    >
      <div>
        <h1 style={{ fontSize: "var(--kz-font-size-xl)", margin: 0 }}>{title}</h1>
        {subtitle && (
          <p
            style={{
              margin: "var(--kz-space-1) 0 0",
              color: "var(--kz-color-ink-muted)",
              fontSize: "var(--kz-font-size-sm)",
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div style={{ display: "flex", gap: "var(--kz-space-2)" }}>{actions}</div>}
    </header>
  );
}

/**
 * 一覧が空のとき。**「0件」だけ出さない**＝次に何をすればいいかを書く。
 *
 * ★M1.7＝`action` を足せる（省略時は従来と1ピクセルも変わらない＝3SPA 共有の後方互換）。
 *   文章で「〜から選んでください」と書いてあっても、**そこへ行く手段が同じ場所に無い**と
 *   利用者は下部タブを探すことになる。行き先が明確な空状態にだけ渡す。
 */
export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <Card style={{ textAlign: "center", color: "var(--kz-color-ink-muted)" }}>
      <p style={{ margin: 0, fontSize: "var(--kz-font-size-md)" }}>{title}</p>
      {hint && <p style={{ margin: "var(--kz-space-2) 0 0", fontSize: "var(--kz-font-size-sm)" }}>{hint}</p>}
      {action && (
        <div style={{ marginTop: "var(--kz-space-4)", display: "flex", justifyContent: "center" }}>{action}</div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* 状態の表示                                                          */
/* ------------------------------------------------------------------ */

export type BadgeTone = "neutral" | "primary" | "warn" | "danger" | "ok" | "transit" | "role";

const TONE: Record<BadgeTone, { bg: string; fg: string; border: string }> = {
  neutral: {
    bg: "var(--kz-status-neutral-weak)",
    fg: "var(--kz-color-ink-muted)",
    border: "var(--kz-status-neutral-border)",
  },
  primary: {
    bg: "var(--kz-color-primary-weak)",
    fg: "var(--kz-color-primary)",
    border: "var(--kz-color-primary-line)",
  },
  warn: {
    bg: "var(--kz-status-warn-weak)",
    fg: "var(--kz-status-warn)",
    border: "var(--kz-status-warn-border)",
  },
  danger: {
    bg: "var(--kz-status-danger-weak)",
    fg: "var(--kz-status-danger)",
    border: "var(--kz-status-danger-border)",
  },
  ok: {
    bg: "var(--kz-status-ok-weak)",
    fg: "var(--kz-status-ok)",
    border: "var(--kz-status-ok-border)",
  },
  // 配送中＝緑一色に潰さず青で1段だけ分ける（進行段階が一覧で読める）
  transit: {
    bg: "var(--kz-status-transit-weak)",
    fg: "var(--kz-status-transit)",
    border: "var(--kz-status-transit-border)",
  },
  role: {
    bg: "var(--kz-role-admin-weak)",
    fg: "var(--kz-role-admin)",
    border: "var(--kz-role-admin-border)",
  },
};

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: BadgeTone }) {
  const t = TONE[tone];
  return (
    <span
      // ★className はスタイルの**アンカーだけ**（M1.9・D-101）＝kit.css に .kz-badge の定義は無い
      //   ＝3SPA とも見た目不変。liff.css だけが pill 化（radius 999）の上書きに使う。
      className="kz-badge"
      style={{
        display: "inline-block",
        padding: "2px var(--kz-space-2)",
        borderRadius: "var(--kz-radius-sm)",
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontSize: "var(--kz-font-size-sm)",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** 卸の業務語彙（LC-08 の左）→ 見た目の強さ。**語彙そのものはサーバが返す**。 */
export function statusTone(status: string): BadgeTone {
  // 一覧で**進行段階が色で読める**ようにする（U1 差分4）。以前は「取消/未確認/納品済」以外が
  // すべて primary で、確定・出荷済み・配送中が同じ緑に潰れていた。
  if (status === "取消") return "danger";
  if (status === "未確認") return "warn";
  if (status === "配送中") return "transit";
  if (status === "納品済") return "ok";
  return "primary"; // 確認済・出荷指示済
}

/** チャネル（05 §6.1 C-02）。**LINEトークは無い**（D-39・D-54⑥）＝web は「アプリ」。 */
export const CHANNEL_LABEL: Record<string, string> = {
  fax: "FAX",
  mail: "メール",
  tel: "電話",
  web: "アプリ",
};

/**
 * 5段の進捗バー（FR-104・LC-08）＝**A-03 と C-03 が同じ部品を使う**。
 *
 * 段の名前も現在位置も**サーバが返した値をそのまま描く**（FR-107）＝
 * 画面が自前で「次はこれのはず」と計算しない＝卸と職人で違う状態が出ない。
 */
export function ProgressBar({
  steps,
  currentIndex,
  size = "md",
  variant = "dots",
}: {
  steps: string[];
  /** -1 ＝ 並びの外（取消）＝どの段も光らせない。 */
  currentIndex: number;
  size?: "md" | "lg";
  /** ★M1.9（D-101）＝発注側の一覧カード用に**バー型**を追加。既定は dots＝console は1pxも変わらない。
   *  bar＝Design（order-app.css `.obar`）の5分割横バー：高さ5px・radius3・done/cur が primary・
   *  cur に primary-weak のリング・下に段名（9.5px・done/cur は primary-hover/700）。 */
  variant?: "dots" | "bar";
}) {
  if (variant === "bar") {
    return (
      <div aria-label="進捗" style={{ display: "flex", gap: 3 }}>
        {steps.map((step, i) => {
          const done = currentIndex >= 0 && i <= currentIndex;
          const current = i === currentIndex;
          return (
            <span
              key={step}
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: "100%",
                  height: 5,
                  borderRadius: 3,
                  background: done ? "var(--kz-color-primary)" : "var(--kz-color-surface-sunken)",
                  boxShadow: current ? "0 0 0 2px var(--kz-color-primary-weak)" : undefined,
                }}
              />
              <span
                // 現在の段を機械で特定できるようにする（E2E の指し先はドット型と同じ）
                data-testid={current ? "progress-current" : undefined}
                style={{
                  fontSize: 9.5,
                  whiteSpace: "nowrap",
                  fontWeight: done || current ? 700 : 400,
                  color: done || current ? "var(--kz-color-primary-hover)" : "var(--kz-color-ink-faint)",
                }}
              >
                {step}
              </span>
            </span>
          );
        })}
      </div>
    );
  }
  const dot = size === "lg" ? 28 : 18;
  return (
    <ol
      aria-label="進捗"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
        listStyle: "none",
        margin: 0,
        padding: 0,
      }}
    >
      {steps.map((step, i) => {
        const done = currentIndex >= 0 && i <= currentIndex;
        const current = i === currentIndex;
        return (
          <li key={step} style={{ textAlign: "center", position: "relative", minWidth: 0 }}>
            {i > 0 && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  top: dot / 2 - 1,
                  right: "50%",
                  width: "100%",
                  height: 2,
                  background: done ? "var(--kz-color-primary)" : "var(--kz-color-border)",
                }}
              />
            )}
            <span
              aria-hidden
              style={{
                position: "relative",
                display: "block",
                width: dot,
                height: dot,
                margin: "0 auto",
                borderRadius: "50%",
                background: done ? "var(--kz-color-primary)" : "var(--kz-color-surface)",
                border: `2px solid ${done ? "var(--kz-color-primary)" : "var(--kz-color-border)"}`,
                boxShadow: current ? "0 0 0 4px var(--kz-color-primary-weak)" : undefined,
              }}
            />
            <span
              // 現在の段を機械で特定できるようにする（E2E が「今どの段か」を検証する点）
              data-testid={current ? "progress-current" : undefined}
              style={{
                display: "block",
                marginTop: "var(--kz-space-2)",
                fontSize: size === "lg" ? "var(--kz-font-size-md)" : "var(--kz-font-size-sm)",
                fontWeight: current ? 700 : 400,
                color: current ? "var(--kz-color-ink)" : "var(--kz-color-ink-muted)",
                overflowWrap: "anywhere",
              }}
            >
              {step}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/* ------------------------------------------------------------------ */
/* 表・金額                                                            */
/* ------------------------------------------------------------------ */

/** 金額の表示。**null は「—」**＝0 と書かない（未確定を確定に見せない・FR-82）。 */
export function Money({ value, suffix = "円" }: { value: string | null; suffix?: string }) {
  if (value === null || value === undefined) {
    return (
      <span style={{ color: "var(--kz-color-ink-muted)" }} title="単価が未確定です">
        —
      </span>
    );
  }
  return (
    <span className="kz-num">
      {Number(value).toLocaleString("ja-JP")}
      {suffix}
    </span>
  );
}

export function Table({ head, children }: { head: ReactNode[]; children: ReactNode }) {
  return (
    // 05 §7 の意図＝**表は自分の中で横スクロール**（ページ本体を横スクロールさせない）。
    // 縦は kit.css の `.kz-table-wrap`（≥1024px で高さ上限＋スクロール）＝見出しの sticky を
    // 成立させるため（詳細は kit.css のコメント）。
    <div className="kz-table-wrap" style={{ overflowX: "auto" }}>
      <table
        className="kz-table"
        style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--kz-font-size-sm)" }}
      >
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: "left",
                  padding: "var(--kz-space-2) var(--kz-space-3)",
                  borderBottom: "1px solid var(--kz-color-border)",
                  color: "var(--kz-color-ink-muted)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <td
      style={{
        padding: "var(--kz-space-3)",
        borderBottom: "1px solid var(--kz-color-border)",
        // U9①：既定は**上下中央**。行の中で背の高いセル（select・ボタン）があると
        // top では他のセルが上に張り付いて読みにくい。上寄せが要る表は style で個別指定する。
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

/* ------------------------------------------------------------------ */
/* 確認・通知                                                          */
/* ------------------------------------------------------------------ */

/**
 * 実行前確認（NFR-04）。**承認・取消・差戻しは必ずここを通す**（05 §5.2）。
 *
 * `reason` を required にすると理由入力欄が出る（FR-34/FR-80＝取消・差戻しは理由必須）。
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = "primary",
  requireReason = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  requireReason?: boolean;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--kz-color-scrim)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "grid",
        placeItems: "center",
        padding: "var(--kz-space-4)",
        zIndex: 50,
      }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const reason = String(new FormData(e.currentTarget).get("reason") ?? "");
          onConfirm(reason);
        }}
        className="kz-modal-panel"
        style={{
          background: "var(--kz-color-surface)",
          borderRadius: "var(--kz-radius-lg)",
          padding: "var(--kz-space-5)",
          width: "min(480px, 100%)",
          // M1.7＝確認ダイアログもモーダル層＝一番深い影（カードと同じ影だと浮いて見えない）
          boxShadow: "var(--kz-shadow-lg)",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-lg)" }}>{title}</h2>
        {body && (
          <div style={{ margin: "var(--kz-space-3) 0", fontSize: "var(--kz-font-size-sm)" }}>{body}</div>
        )}
        {requireReason && (
          <label style={{ display: "block", marginTop: "var(--kz-space-3)" }}>
            <span style={{ fontSize: "var(--kz-font-size-sm)", fontWeight: 600 }}>理由（必須）</span>
            <textarea
              name="reason"
              required
              rows={3}
              style={{
                width: "100%",
                marginTop: "var(--kz-space-1)",
                padding: "var(--kz-space-2)",
                borderRadius: "var(--kz-radius-sm)",
                border: "1px solid var(--kz-color-border)",
                font: "inherit",
              }}
            />
          </label>
        )}
        <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-5)" }}>
          {/* ★**`type="button"` は必須**（M1.7）＝この2つは `<form onSubmit={onConfirm}>` の中にある。
              `Button` は type を設定しない（HTML の既定は submit）ので、外すと「やめる」を押した
              瞬間に**確定操作が走る**。E2E は「やめる」を一度も押さないので、テストでは捕まらない。 */}
          <button
            type="button"
            onClick={onCancel}
            className="kz-btn"
            style={{ flex: 1, padding: "var(--kz-space-3)" }}
          >
            やめる
          </button>
          <button
            type="submit"
            disabled={busy}
            className={tone === "danger" ? "kz-btn kz-btn--danger" : "kz-btn kz-btn--primary"}
            style={{ flex: 1, padding: "var(--kz-space-3)", fontWeight: 700, cursor: busy ? "wait" : undefined }}
          >
            {busy ? "処理中…" : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * エラー表示。**request_id を出す**（05 §5.1）＝問い合わせで突合できる。
 */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error as { message?: string; requestId?: string };
  return (
    <div
      role="alert"
      style={{
        background: "var(--kz-status-danger-weak)",
        color: "var(--kz-status-danger)",
        border: "1px solid currentColor",
        borderRadius: "var(--kz-radius-sm)",
        padding: "var(--kz-space-3)",
        fontSize: "var(--kz-font-size-sm)",
        marginBottom: "var(--kz-space-3)",
      }}
    >
      {e.message ?? "エラーが発生しました。"}
      {e.requestId && (
        <span style={{ display: "block", marginTop: "var(--kz-space-1)", opacity: 0.8 }}>
          お問い合わせ番号: <span className="kz-num">{e.requestId}</span>
        </span>
      )}
    </div>
  );
}

export function Spinner({ label = "読み込み中…" }: { label?: string }) {
  return (
    <p
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--kz-space-2)",
        color: "var(--kz-color-ink-muted)",
        fontSize: "var(--kz-font-size-sm)",
      }}
    >
      {/* M1.7＝文字だけだと「止まっているのか動いているのか」が分からない。回る図形を添える
          （`prefers-reduced-motion` では kit.css が止める）。**文言は変えない**＝ここを頼りに
          読んでいる手順書がある。 */}
      <span className="kz-spinner" aria-hidden="true" />
      {label}
    </p>
  );
}

/**
 * スケルトン（M1.7）＝これから出る内容の**形**で待たせる。
 *
 * ★**データ由来の要素をロード中に描かない**（表・testid 付きの行など）。E2E には
 * 「`table` が見えた＝データが確定した」を前提に本文を読む検査があり、器だけ先に
 * 出すと空の中身を読んで落ちる。スケルトンは常に「素の div の帯」で描くこと。
 */
export function Skeleton({
  rows = 3,
  height = 14,
  width = "100%",
  style,
}: {
  rows?: number;
  height?: number;
  /** 単一行のときの幅。行が複数のときは末尾だけ少し短くして「文章の終わり」に見せる。 */
  width?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      aria-hidden="true"
      style={{ display: "flex", flexDirection: "column", gap: "var(--kz-space-2)", ...style }}
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="kz-skeleton"
          style={{ height, width: rows > 1 && i === rows - 1 ? "62%" : width }}
        />
      ))}
    </div>
  );
}

/** 日付・時刻は JST 表示（04 §1 #9＝保存は UTC）。 */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  // `desired_date` は date（時刻もTZも無い）＝new Date() に食わせると UTC 解釈されて
  // 前日にずれる。**文字列のまま組み立てる**。
  const [y, m, d] = iso.split("-").map(Number);
  const weekday = ["日", "月", "火", "水", "木", "金", "土"][new Date(y, m - 1, d).getDay()];
  return `${m}/${d}(${weekday})`;
}

/* ------------------------------------------------------------------ */
/* シェル・遷移まわりの小物（D-82・U3/U7）                              */
/* ------------------------------------------------------------------ */

/**
 * パンくず（U3）＝**いまどこにいて、どこへ戻れるか**を詳細画面に出す。
 *
 * これが無いと、受注詳細の「← 受注箱へ」のような**単発の戻りリンク**を各画面が自前で持つことに
 * なり、受注一覧から来ても受注箱へ帰る（＝経路と食い違う）といったズレが起きる（実際に起きていた）。
 * 末尾は現在地＝リンクにしない。
 */
export function Breadcrumb({
  items,
  linkAs: LinkAs,
}: {
  items: { label: string; to?: string }[];
  /**
   * リンクに使うコンポーネント（通常は react-router の `Link`）。
   *
   * ★**素の `<a href>` を既定にしない**＝SPA の外へ出てフルリロードになり、
   *   セッション再取得と再レンダリングで一瞬「読み込み中」が挟まる（画面が点滅する）。
   *   `packages/ui` は router に依存しない方針なので、**使う側が渡す**形にした。
   */
  // ★型は `ElementType`＝**JSX として描く**。関数として呼ぶ形（`LinkAs({...})`）にすると、
  //   react-router の `Link` は forwardRef オブジェクトで**関数ではない**ため
  //   実行時に「LinkAs is not a function」で落ちる（型は通るのに白画面になる）。
  linkAs?: React.ElementType;
}) {
  return (
    <nav className="kz-breadcrumb" aria-label="パンくず">
      {items.map((item, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "var(--kz-space-2)" }}>
          {i > 0 && <span aria-hidden="true">/</span>}
          {item.to ? (
            LinkAs ? (
              <LinkAs to={item.to}>{item.label}</LinkAs>
            ) : (
              // 渡されなかったときだけ素のリンク（ui 単体で使う場合の保険）
              <a href={item.to}>{item.label}</a>
            )
          ) : (
            <span className="kz-bc-cur">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/**
 * フィルタチップ（デモの .chip）＝選択状態が一目で分かる。
 *
 * `<select>` のドロップダウンだと「いま何で絞っているか」が畳まれて見えない（U1 差分6）。
 */
export function Chip({
  children,
  on = false,
  ...rest
}: { children: ReactNode; on?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={on ? "kz-chip is-on" : "kz-chip"} aria-pressed={on} {...rest}>
      {children}
    </button>
  );
}

/**
 * 汎用モーダル（U7 の説明・U8 の招待など「確認以外」の器）。
 *
 * `ConfirmDialog`（確認専用・480px）と**器を2系統に散らかさない**ために置く。
 * ページ側で overlay を自作すると z-index が不揃いになり、`ErrorNote` が覆いの裏に回る
 * （S7 で実際に起きた「押しても何も起きないように見える」の原因）。
 */
export function Modal({
  title,
  onClose,
  children,
  width = 760,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--kz-color-scrim)",
        // M1.7＝覆いを少しぼかす＝下の画面が「奥にある」と読める（HeroUI の backdrop blur）
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "40px 20px",
        overflowY: "auto",
        zIndex: 50, // ConfirmDialog と同じ層（ページ自作の 10 系統は使わない）
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="kz-modal-panel"
        style={{
          background: "var(--kz-color-surface)",
          borderRadius: "var(--kz-radius-md)",
          boxShadow: "var(--kz-shadow-lg)",
          maxWidth: width,
          width: "100%",
          padding: "var(--kz-space-5)",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          data-testid="modal-close"
          style={{
            position: "absolute",
            top: 10,
            right: 12,
            border: "none",
            background: "none",
            fontSize: 22,
            lineHeight: 1,
            color: "var(--kz-color-ink-muted)",
            cursor: "pointer",
            padding: 6,
          }}
        >
          ×
        </button>
        <h3
          style={{
            margin: "0 0 var(--kz-space-4)",
            fontSize: "var(--kz-font-size-lg)",
            color: "var(--kz-color-ink)",
          }}
        >
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

/**
 * トースト（操作の成功を返す）。**失敗は `ErrorNote`**（消える通知に失敗を入れない＝
 * 読み落とすと「何が起きたか分からない」になる）。
 */
export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="kz-toast-wrap">
      <div className="kz-toast" role="status" data-testid="toast">
        {message}
      </div>
    </div>
  );
}
